// EOP reporting-period helpers.
//
// The End of Period report asks "which period are you closing?" instead of a
// raw date range. Period end dates come from the store's accounting calendar
// (inventory_period_config) when one is set — 4-5-4 / 5-4-4 / fiscal / custom
// schedules carry an explicit period_end_dates list, weekly schedules carry a
// week_end_day — otherwise plain calendar month ends are used.

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;

export const isoFromParts = (y, monthIndex, day) => {
  const m = String(monthIndex + 1).padStart(2, '0');
  const d = String(day).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const isoToDate = (iso) => {
  if (!iso || !ISO_RE.test(iso)) return null;
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const DAY_MS = 24 * 60 * 60 * 1000;

export const daysBetweenISO = (a, b) => {
  const da = isoToDate(a);
  const db = isoToDate(b);
  if (!da || !db) return null;
  return Math.round((db.getTime() - da.getTime()) / DAY_MS);
};

// Last day of the month containing (year, monthIndex).
export const monthEndISO = (year, monthIndex) => {
  const d = new Date(year, monthIndex + 1, 0);
  return isoFromParts(d.getFullYear(), d.getMonth(), d.getDate());
};

const monthLabel = (iso) => {
  const d = isoToDate(iso);
  if (!d) return iso;
  const month = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const ends = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${month} (ends ${ends})`;
};

const shortDate = (iso) => {
  const d = isoToDate(iso);
  if (!d) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * Build the period-end choices for the dropdown, newest first.
 *
 * - Configured accounting calendar (4-5-4, 5-4-4, fiscal, custom_day…):
 *   every valid date in cfg.period_end_dates.
 * - Weekly schedule: the configured week-end day for the past `weeksBack`
 *   weeks (plus the upcoming one).
 * - Otherwise: calendar month ends for the past `monthsBack` months (plus the
 *   current month's end).
 */
export function buildPeriodEndOptions(cfg, todayISO, { monthsBack = 24, weeksBack = 26 } = {}) {
  const today = isoToDate(todayISO) || new Date();

  if (cfg?.enabled && Array.isArray(cfg.period_end_dates) && cfg.period_end_dates.length > 0) {
    const sorted = [...new Set(cfg.period_end_dates.filter((d) => ISO_RE.test(d)))].sort();
    return sorted
      .map((iso, i) => ({ iso, label: `Period ${i + 1} — ends ${shortDate(iso)}` }))
      .reverse();
  }

  if (cfg?.enabled && cfg.schedule_type === 'weekly') {
    const weekEndDay = typeof cfg.week_end_day === 'number' ? cfg.week_end_day : 0;
    // Next occurrence of the week-end day on/after today, then walk backwards.
    const next = new Date(today);
    next.setDate(next.getDate() + ((weekEndDay - next.getDay() + 7) % 7));
    const opts = [];
    for (let i = 0; i <= weeksBack; i++) {
      const d = new Date(next);
      d.setDate(d.getDate() - i * 7);
      const iso = isoFromParts(d.getFullYear(), d.getMonth(), d.getDate());
      opts.push({ iso, label: `Week ending ${shortDate(iso)}` });
    }
    return opts;
  }

  // Calendar month ends (also used for month_end schedules — and the
  // 4-5-4 / 5-4-4 cases above already supplied their corresponding dates).
  const opts = [];
  for (let i = 0; i <= monthsBack; i++) {
    const iso = monthEndISO(today.getFullYear(), today.getMonth() - i);
    opts.push({ iso, label: monthLabel(iso) });
  }
  return opts;
}

/**
 * The option nearest to today by absolute distance ("if it is June 3rd we
 * assume they closed May"). Ties prefer the date already in the past.
 */
export function nearestPeriodEndISO(options, todayISO) {
  const today = isoToDate(todayISO);
  if (!today || !options?.length) return options?.[0]?.iso ?? null;
  let best = null;
  let bestDist = Infinity;
  for (const opt of options) {
    const d = isoToDate(opt.iso);
    if (!d) continue;
    const dist = Math.abs(d.getTime() - today.getTime());
    const wins = dist < bestDist
      || (dist === bestDist && best != null && opt.iso < best); // tie → earlier (past)
    if (wins) { best = opt.iso; bestDist = dist; }
  }
  return best;
}

/**
 * The previous period boundary for a chosen end date: the largest date in the
 * store's counting structure strictly before endISO. Works for custom dates
 * too (e.g. a custom June 15 close on a month-end structure → May 31).
 */
export function previousPeriodEndISO(cfg, endISO) {
  const end = isoToDate(endISO);
  if (!end) return null;

  if (cfg?.enabled && Array.isArray(cfg.period_end_dates) && cfg.period_end_dates.length > 0) {
    const before = cfg.period_end_dates
      .filter((d) => ISO_RE.test(d) && d < endISO)
      .sort();
    if (before.length > 0) return before[before.length - 1];
    // endISO precedes every configured date — fall through to month ends.
  }

  if (cfg?.enabled && cfg.schedule_type === 'weekly') {
    const weekEndDay = typeof cfg.week_end_day === 'number' ? cfg.week_end_day : 0;
    const d = new Date(end);
    // Walk back to the previous week-end day strictly before endISO.
    d.setDate(d.getDate() - (((d.getDay() - weekEndDay + 7) % 7) || 7));
    return isoFromParts(d.getFullYear(), d.getMonth(), d.getDate());
  }

  // Month-end structure: the last month end strictly before endISO — the
  // previous month's end whether endISO is this month's close or a custom
  // mid-month date.
  return monthEndISO(end.getFullYear(), end.getMonth() - 1);
}
